import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiSearch, FiCheckCircle, FiMail } = FiIcons;

const SupportTicketManagement = () => {
  const navigate = useNavigate();
  const { supportTickets = [], updateSupportTicketStatus, fetchSupportTickets, loading } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (typeof fetchSupportTickets === 'function') {
      fetchSupportTickets();
    }
  }, []);

  const filteredTickets = (supportTickets || []).filter(ticket => {
    if (!ticket) return false;
    const matchesSearch = 
      (ticket.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.user_email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <div className="p-8 text-center">Laster supportsaker...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg">
              <SafeIcon icon={FiArrowLeft} />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Supporthenvendelser</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Søk i emne eller e-post..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value="all">Alle statuser</option>
                <option value="open">Åpen</option>
                <option value="resolved">Løst</option>
            </select>
        </div>

        <div className="space-y-4">
            {filteredTickets.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm italic text-gray-500 border border-dashed border-gray-200">
                    Ingen henvendelser funnet.
                </div>
            ) : (
                filteredTickets.map(ticket => (
                    <div key={ticket.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusColor(ticket.status || 'open')}`}>
                                        {ticket.status === 'resolved' ? 'Løst' : 'Åpen'}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                        {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : 'Dato ukjent'}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1">{ticket.subject || 'Inget emne'}</h3>
                                <p className="text-sm text-blue-600 mb-4">{ticket.user_email || 'Ukjent avsender'}</p>
                                <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 whitespace-pre-wrap border border-gray-100">
                                    {ticket.message || 'Ingen meldingstekst.'}
                                </div>
                            </div>
                            <div className="flex flex-row md:flex-col gap-2 min-w-[140px]">
                                {ticket.status !== 'resolved' && (
                                    <button
                                        onClick={() => updateSupportTicketStatus(ticket.id, 'resolved')}
                                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-green-700 transition-colors"
                                    >
                                        <SafeIcon icon={FiCheckCircle} /> Løs
                                    </button>
                                )}
                                <a
                                    href={`mailto:${ticket.user_email}?subject=SV: ${ticket.subject}`}
                                    className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors border border-blue-100"
                                >
                                    <SafeIcon icon={FiMail} /> Svar
                                </a>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
      </div>
    </div>
  );
};

export default SupportTicketManagement;